import { useEffect, useState } from 'react'

/**
 * 输入框草稿，持久化到 localStorage（按 space/session id 区分），用来扛过
 * **in-app 浏览器在调起原生相机时的整页 reload**：微信内置浏览器（WKWebView）
 * 和 iOS standalone PWA 打开 `<input capture>` 拍照、返回时会把页面 reload，
 * React 内存 state 全丢、用户刚打的字消失（issue #133）。
 *
 * reload = 组件重新挂载 = 下面的懒初始化重新从 localStorage 读回草稿 → 文字还在。
 * 发送成功后调用方 `setDraft('')` 清空（value 空时 effect 删掉 storage key）。
 *
 * 说明：只处理「同一 id 下的 reload 恢复」这一主场景；不处理组件不卸载、id 原地
 * 切换的边角情况（dispatch 导航基本是 列表→会话 会重挂载，且相机 reload 是整页重载）。
 */
export function useDraft(key: string): [string, (v: string) => void] {
  const storageKey = `cortex-m-draft:${key}`
  const [value, setValue] = useState<string>(() => {
    try {
      return localStorage.getItem(storageKey) ?? ''
    } catch {
      return ''
    }
  })
  useEffect(() => {
    try {
      if (value) localStorage.setItem(storageKey, value)
      else localStorage.removeItem(storageKey)
    } catch {
      /* private mode / quota — 草稿就不持久化，退回原内存行为 */
    }
  }, [storageKey, value])
  return [value, setValue]
}
